// @ts-strict-ignore
import { DOCUMENT } from '@angular/common';
import { ElementRef, Inject } from '@angular/core';
import { ComponentStore } from '@ngrx/component-store';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { fromEvent, map, Observable, of, race, switchMap, tap } from 'rxjs';
import { filter } from 'rxjs/operators';

import {
  StageImageType,
  StageParameter,
  StageV2BaseInformation,
  StageView,
  StageViewDefinition,
  StageViewState
} from 'src/app/api/owcc/models';
import { Mq } from 'src/app/core/config/mq.config';
import { stageParamsToQueryParams } from 'src/app/shared/stage/awesome-stage/awesome-stage.helpers';
import { fromCarl, selectIsUserEmployee } from 'src/app/store/common/common.selectors';
import { selectRouteState } from 'src/app/store/router/router.selectors';
import { noop } from 'src/app/utils/functions/noop';
import { observeOnResize } from 'src/app/utils/functions/observe-on-resize';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';
import { RATIO_16_9, RATIO_21_9 } from 'src/app/utils/number-constants';

import {
  ButtonTooltipTexts,
  DEFAULT_BUTTON_TOOLTIP_TEXTS,
  RoofStateValue,
  StageBaseData,
  StageConfig,
  StageViewStateValue,
  type StageRatio
} from '../awesome-stage.model';
import { BbdUrlBuilder, createCacheOptimizedPovParam } from '../bbd-url-builder';
import imagePreloadScheduler from '../image-preload-scheduler';

import {
  HIGHRES_IMAGE_WIDTH_MAP,
  isCurrentlyInFullscreenMode,
  Mq1To4,
  Mq5AndAbove
} from './awesome-stage-store-constants';
import { Icon, MimeType, ScreenMode } from './awesome-stage.store.enum';
import { ImageParameters } from './image-parameters.interface';
import { StatePerScreenMode } from './state-per-screen-mode';
import { ThreeSixtyInteriorData } from './three-sixty-interior-data';

// Unfortunately, the pannellum library does not provide typing
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type PannellumViewer = any;

export interface AwesomeStageState {
  stageBaseData: StageBaseData;
  stageConfig: StageConfig;
  currentView: StageView; // current stage view, e.g. EXTERIOR_360 or INTERIOR, can be changed via request or user
  exteriorThreeSixtyIndex: number | undefined;
  isExteriorThreeSixtyStageLoaded: boolean;

  exteriorStageViewState: StatePerScreenMode<StageViewStateValue>;
  interiorStageViewState: StatePerScreenMode<StageViewStateValue>;
  interiorThreeSixtyStageViewState: StatePerScreenMode<StageViewStateValue>;

  exteriorRoofState: StatePerScreenMode<RoofStateValue>;
  interiorRoofState: StatePerScreenMode<RoofStateValue>;
  interiorThreeSixtyRoofState: StatePerScreenMode<RoofStateValue>;

  interiorSliderIndex: number | undefined;
  interiorThreeSixtyLastState: ThreeSixtyInteriorData;
  isInteriorThreeSixtyLoading: boolean;

  isInteriorThreeSixtyError: boolean;
  isFullscreenMode: boolean;
  isInScreenMirroringMode: boolean;
  stageRatio: StageRatio;
  hasFullscreenMode: boolean;
  stageHeight: number;
  controlBarHeight: number;
  windowHeight: number;
  mediaQuery: Mq;
  buttonTooltipTexts: ButtonTooltipTexts | undefined;
  isInThreeSixtyInteriorMode: boolean; // can be true if sphere view images available
  pannellumViewer: PannellumViewer;
  isFullscreenSupported: boolean;
  usersChoiceRoofTopState: RoofStateValue; // User interacted with the rooftop button. we save the status to keep it closed or open when the user navigates to other sections
  isInteriorThreeSixtyOverlayEnabled: boolean;
  hasInteriorThreeSixtyBeenUsed: boolean;
}

const defaultAwesomeStageState: AwesomeStageState = {
  stageBaseData: undefined,
  stageConfig: undefined,
  currentView: undefined,
  exteriorThreeSixtyIndex: undefined,
  isExteriorThreeSixtyStageLoaded: false,

  exteriorStageViewState: undefined,
  interiorStageViewState: undefined,
  interiorThreeSixtyStageViewState: undefined,

  exteriorRoofState: undefined,
  interiorRoofState: undefined,
  interiorThreeSixtyRoofState: undefined,

  interiorSliderIndex: 0,
  interiorThreeSixtyLastState: undefined,
  isInteriorThreeSixtyLoading: false,

  isInteriorThreeSixtyError: false,
  isInScreenMirroringMode: false,
  isFullscreenMode: false,
  stageRatio: RATIO_16_9,
  hasFullscreenMode: false,
  stageHeight: 0,
  controlBarHeight: 0,
  windowHeight: 0,
  mediaQuery: undefined,
  buttonTooltipTexts: DEFAULT_BUTTON_TOOLTIP_TEXTS,
  isInThreeSixtyInteriorMode: false,
  pannellumViewer: undefined,
  isFullscreenSupported: false,
  isInteriorThreeSixtyOverlayEnabled: false,
  hasInteriorThreeSixtyBeenUsed: false,
  usersChoiceRoofTopState: undefined
};

/**
 * This is a base class for the AwesomeStageStore. Here only functions or selectors which are
 * purely related to the state are are added. For specific functionalilty for e.g interior or
 * exterior, please use the AwesomeStageStore
 */
export abstract class AwesomeStageCommonStore extends ComponentStore<AwesomeStageState> {
  public readonly stageBaseData$: Observable<StageBaseData> = this.select(state => state.stageBaseData);

  public readonly stageMetaData$ = this.select(this.stageBaseData$, stageBaseData => stageBaseData?.metaData);

  public readonly stageConfig$: Observable<StageConfig> = this.select(state => state.stageConfig);

  public readonly baseInfo$ = this.select(this.stageMetaData$, stageMetaData => stageMetaData?.baseInfo);

  public readonly currentView$: Observable<StageView> = this.select(state => state.currentView);

  public readonly isInFullscreenMode$ = this.select(state => state.isFullscreenMode);

  public readonly isFullscreenSupported$ = this.select(state => state.isFullscreenSupported);

  public readonly stageHeight$: Observable<number> = this.select(state => state.stageHeight);

  public readonly controlBarHeight$: Observable<number> = this.select(state => state.controlBarHeight);

  public readonly windowHeight$: Observable<number> = this.select(state => state.windowHeight);

  public readonly stageRatio$: Observable<StageRatio> = this.select(stage => stage.stageRatio);

  public readonly isInScreenMirroringMode$ = this.select(state => state.isInScreenMirroringMode);

  public readonly cosyId$ = this.select(this.stageBaseData$, stageBaseData => stageBaseData?.cosyId);

  public readonly buttonTooltipTexts$ = this.select(state => state?.buttonTooltipTexts);

  public readonly hasImageError$ = this.select(this.stageBaseData$, stageBaseData => stageBaseData?.hasImageError);

  public readonly currentFallbackImage$ = this.select(this.baseInfo$, baseInfo =>
    new BbdUrlBuilder(baseInfo?.fallbackImageUrl)?.toString()
  );

  public readonly mediaQuery$ = this.select(state => state.mediaQuery).pipe(filterTruthy());

  public readonly isMq1$ = this.select(this.mediaQuery$, mediaQuery => mediaQuery === Mq.Mq1);

  /**
   * In fullscreen mode there might be not enough height (with a wide screen monitor), so define a max height to show the full stage
   */
  public readonly fullscreenMaxHeightInPx$: Observable<string> = this.select(
    this.isInFullscreenMode$,
    this.windowHeight$,
    this.controlBarHeight$,
    (isInFullscreenMode, windowHeight, controlBarHeight) =>
      isInFullscreenMode ? `${windowHeight - controlBarHeight}px` : `${Number.MAX_VALUE}px`
  );

  /**
   * returns true if in fullscreen mode and _not_ MQ1.
   * MQ1 is pretty small, no space for such a large close button (overlays with the slider next button).
   * It's still possible to exit fullscreen via the toggle button.
   */
  public readonly displayFullscreenCloseButton$ = this.select(
    this.isInFullscreenMode$,
    this.isMq1$,
    (isInFullscreenMode, isMq1) => isInFullscreenMode && !isMq1
  );

  public readonly isExteriorView$: Observable<boolean> = this.select(
    this.currentView$,
    view => view === StageView.Exterior360
  );

  public readonly exteriorRoofState$: Observable<StatePerScreenMode<RoofStateValue>> = this.select(
    state => state.exteriorRoofState
  );

  public readonly interiorRoofState$: Observable<StatePerScreenMode<RoofStateValue>> = this.select(
    state => state.interiorRoofState
  );

  public readonly interiorThreeSixtyRoofState$: Observable<StatePerScreenMode<RoofStateValue>> = this.select(
    state => state.interiorThreeSixtyRoofState
  );

  public readonly isInteriorThreeSixtyError$: Observable<boolean> = this.select(
    state => state.isInteriorThreeSixtyError
  );

  public readonly currentViewDefinition$: Observable<StageViewDefinition | undefined> = this.select(
    this.stageMetaData$,
    this.currentView$,
    (stageMetaData, view) => stageMetaData?.views?.[view as string]
  );

  /**
   * Also considers whether in fullscreen mode or not!
   */
  public readonly currentViewDefinitionStatesByScreenMode$ = this.select(
    this.currentViewDefinition$,
    this.isInFullscreenMode$,
    (viewDefinition, isInFullscreenMode) =>
      isInFullscreenMode && viewDefinition?.fullScreenDefinition
        ? viewDefinition?.fullScreenDefinition?.states
        : viewDefinition?.states
  );

  public readonly imageWidthByMq$: Observable<number> = this.select(
    this.mediaQuery$,
    mediaQuery => HIGHRES_IMAGE_WIDTH_MAP[mediaQuery as Mq1To4 | Mq5AndAbove] || HIGHRES_IMAGE_WIDTH_MAP.default
  );

  public readonly usersChoiceRoofTopState$ = this.select(state => state.usersChoiceRoofTopState);

  public readonly stageViewStateChangeForCurrentViewOffered$ = this.select(
    this.currentViewDefinitionStatesByScreenMode$,
    stageStates => this.isDayAndNightStageViewStateAvailable(stageStates)
  );

  public readonly isUserEmployeeAndIsScreenMirroringActive$ = this.select(
    this.store.select(selectIsUserEmployee),
    this.store.pipe(fromCarl.selectConfigOnce<boolean>('screenMirroring.active', false)),
    (isUserEmployee, isScreenMirroringActive) => isUserEmployee && isScreenMirroringActive
  );

  public readonly stageViewToggleButtonTooltip$: Observable<string | undefined> = this.select(
    this.currentView$,
    this.buttonTooltipTexts$,
    (currentView, buttonTooltipTexts) =>
      currentView === StageView.Exterior360 ? buttonTooltipTexts.toggleInterior : buttonTooltipTexts.toggleExterior
  );

  public readonly trunkButtonTooltip$: Observable<string | undefined> = this.select(
    this.buttonTooltipTexts$,
    buttonTooltipTexts => buttonTooltipTexts.slideToTrunk
  );

  public readonly fullscreenButtonTooltip$ = this.select(
    this.isInFullscreenMode$,
    this.buttonTooltipTexts$,
    (isInFullscreenMode, buttonTooltipTexts) =>
      isInFullscreenMode ? buttonTooltipTexts.leaveFullscreen : buttonTooltipTexts.enterFullscreen
  );

  public readonly screenMirroringButtonTooltip$ = this.select(
    this.isInScreenMirroringMode$,
    this.buttonTooltipTexts$,
    (isInScreenMirroringMode, buttonTooltipTexts) =>
      isInScreenMirroringMode ? buttonTooltipTexts.exitScreenMirroring : buttonTooltipTexts.startScreenMirroring
  );

  public readonly fullscreenModeOffered$ = this.select(state => state.hasFullscreenMode);

  public readonly fullscreenModeStateIcon$ = this.select(this.isInFullscreenMode$, isInFullscreenMode =>
    isInFullscreenMode ? Icon.FullscreenExit : Icon.Fullscreen
  );

  public readonly screenMirroringStateIcon$ = this.select(this.isInScreenMirroringMode$, isInScreenMirroringMode =>
    isInScreenMirroringMode ? Icon.ScreenMirroringExit : Icon.ScreenMirroring
  );

  public readonly disclaimer$ = this.select(this.stageConfig$, stageConfig => stageConfig.disclaimer);

  public readonly roofStateChangeForCurrentViewOffered$ = this.select(this.currentViewDefinition$, viewDefinition =>
    this.isOpenedAndClosedRoofStateAvailable(viewDefinition?.states)
  );

  /**
   * White disclaimer text in fullscreen mode, black otherwise.
   */
  public readonly disclaimerColorStyle$ = this.select(this.isInFullscreenMode$, isInFullscreenMode =>
    isInFullscreenMode ? 'white' : 'black'
  );

  public readonly allViewDefinitions$: Observable<StageViewDefinition[]> = this.select(
    this.stageMetaData$,
    stageMetaData => Object.values(stageMetaData?.views || {})
  );

  public readonly stageViewStateChangeOfferedInAnyView$: Observable<boolean> = this.select(
    this.allViewDefinitions$,
    viewDefinitions =>
      viewDefinitions?.some(
        viewDefinition =>
          this.isDayAndNightStageViewStateAvailable(viewDefinition?.states) ||
          this.isDayAndNightStageViewStateAvailable(viewDefinition?.fullScreenDefinition?.states)
      )
  );

  public readonly roofStateChangeOfferedInAnyView$: Observable<boolean> = this.select(
    this.allViewDefinitions$,
    viewDefinitions =>
      viewDefinitions?.some(
        viewDefinition =>
          this.isOpenedAndClosedRoofStateAvailable(viewDefinition?.states) ||
          this.isOpenedAndClosedRoofStateAvailable(viewDefinition?.fullScreenDefinition?.states)
      )
  );

  public readonly processedRouteState$ = this.select(this.store.select(selectRouteState), routeState => {
    let processedRouteState = '';

    if (routeState.isMotorization) {
      processedRouteState = 'motorization'.toUpperCase();
    }
    if (routeState.isSummary) {
      processedRouteState = 'summary'.toUpperCase();
    }
    if (routeState.isConfiguration) {
      processedRouteState = routeState.configurationStep.toUpperCase();
    }

    return processedRouteState;
  });

  public readonly stagePadding$ = this.select(
    this.stageRatio$,
    this.fullscreenMaxHeightInPx$,
    (stageRatio, fullscreenMaxHeightInPx) => `min(calc(100% / ${stageRatio}), ${fullscreenMaxHeightInPx})`
  );

  public readonly stageRatioWithFullscreenConsideration$ = this.select(
    this.stageRatio$,
    this.isInFullscreenMode$,
    (stageRatio, isInFullscreenMode) => (isInFullscreenMode ? RATIO_21_9 : stageRatio)
  );

  public readonly updateHasFullscreenMode = this.effect((hasFullscreenMode$: Observable<boolean>) =>
    hasFullscreenMode$.pipe(
      concatLatestFrom(() => this.isFullscreenSupported$),
      tap(([hasFullscreenMode, isFullscreenSupported]: [boolean, boolean]) => {
        this.patchState({ hasFullscreenMode: hasFullscreenMode && isFullscreenSupported });
      })
    )
  );

  public readonly updateStageConfig = this.effect((stageConfig$: Observable<StageConfig>) =>
    stageConfig$.pipe(
      tap(stageConfig => {
        this.patchState({ stageConfig });
      }),
      tap((stageConfig: StageConfig) => {
        imagePreloadScheduler.configure(stageConfig.exterior.imagePreloading);
      })
    )
  );

  /**
   * Toggles fullscreen mode.
   * ℹ️ we do _not_ patch the state here directly because we observe whether fullscreen mode successfully entered / left, see {@link observeFullscreenModeToUpdateState}.
   */
  public readonly toggleFullscreen = this.effect((element$: Observable<HTMLElement>) =>
    element$.pipe(
      concatLatestFrom(() => this.isInFullscreenMode$),
      tap(([element, isInFullscreenMode]: [HTMLElement, boolean]) => {
        if (isInFullscreenMode) this.cancelFullscreen();
        else this.requestFullscreen(element);
      })
      // intentionally do not patch state here (see method comment above)
    )
  );

  public readonly observeStageHeight = this.effect((stageContainer$: Observable<ElementRef<HTMLElement>>) => {
    const patchStageHeight = (stageHeight: number): void => {
      this.patchState({ stageHeight });
    };

    return stageContainer$.pipe(
      switchMap(stageContainer =>
        observeOnResize(stageContainer.nativeElement).pipe(
          map(() => this.getElementHeight(stageContainer.nativeElement))
        )
      ),
      tap<number>(stageHeight => {
        patchStageHeight(stageHeight);
      })
    );
  });

  public readonly observeControlBarHeight = this.effect((element$: Observable<ElementRef<HTMLElement>>) => {
    const patchControlBarHeight = (controlBarHeight: number): void => {
      this.patchState({ controlBarHeight });
    };

    return element$.pipe(
      filter(element => !!element?.nativeElement),
      switchMap(element =>
        observeOnResize(element.nativeElement).pipe(map(() => this.getElementHeight(element.nativeElement)))
      ),
      tap<number>(controlBarHeight => {
        patchControlBarHeight(controlBarHeight);
      })
    );
  });

  public readonly observeWindowHeight = this.effect((window$: Observable<Window>) => {
    const patchWindowHeight = (windowHeight: number): void => {
      this.patchState({ windowHeight });
    };

    return window$.pipe(
      tap(window => {
        patchWindowHeight(window.innerHeight);
      }),
      switchMap(window => fromEvent(window, 'resize').pipe(map(() => window.innerHeight))),
      tap<number>(windowHeight => {
        patchWindowHeight(windowHeight);
      })
    );
  });

  /**
   * iOS for iPhone does (currently / spring 2023) _not_ support fullscreen mode (iPad does support, see https://caniuse.com/?search=fullscreenEnabled)
   */
  public readonly evaluateFullscreenSupportForPlatform = this.effect(trigger$ =>
    trigger$.pipe(
      concatLatestFrom(() => of(this.document.fullscreenEnabled || this.document.mozFullScreenEnabled)),
      tap(([, isFullscreenSupported]: [void, boolean]) => {
        this.patchState({ isFullscreenSupported });
      })
    )
  );

  /**
   * Observing fullscreen mode is needed to check whether entering the fullscreen mode really took place (without errors)
   * and also to update button state when user left fullscreen mode not via toggle button but escape key.
   */
  public readonly observeFullscreenModeToUpdateState = this.effect(trigger$ =>
    trigger$.pipe(
      switchMap(() =>
        race([fromEvent(this.document, 'fullscreenchange'), fromEvent(this.document, 'webkitfullscreenchange')])
      ),
      map(() => isCurrentlyInFullscreenMode(this.document)),
      tap<boolean>(isInFullscreenMode => {
        this.patchState({ isFullscreenMode: isInFullscreenMode ? true : false });
      })
    )
  );

  public readonly toggleScreenMirroringMode = this.effect(trigger$ =>
    trigger$.pipe(
      concatLatestFrom(() => this.isInScreenMirroringMode$),
      tap(([, isInScreenMirroringMode]) => {
        this.patchState({ isInScreenMirroringMode: !isInScreenMirroringMode });
      })
    )
  );

  public constructor(
    protected readonly store: Store,
    @Inject(DOCUMENT) protected readonly document: Document
  ) {
    super(defaultAwesomeStageState);
  }

  public readonly createStatePerScreenMode = (
    toggleOffered: StatePerScreenMode<boolean>,
    currentState: StatePerScreenMode<StageViewStateValue>
  ): StatePerScreenMode<StageViewStateValue> => {
    const toggle = (state: StageViewStateValue): StageViewStateValue =>
      state === StageViewStateValue.Night ? StageViewStateValue.Day : StageViewStateValue.Night;

    const getStateViewState = (screenMode: ScreenMode, stageViewState: StageViewStateValue): StageViewStateValue =>
      toggleOffered?.getStateByScreenMode(screenMode) ? toggle(stageViewState) : stageViewState;

    return new StatePerScreenMode(
      getStateViewState(ScreenMode.NonFullscreen, currentState.nonFullscreen),
      getStateViewState(ScreenMode.Fullscreen, currentState.fullscreen)
    );
  };

  public readonly updateExteriorStageViewState = (
    exteriorStageViewState: StatePerScreenMode<StageViewStateValue>
  ): void => {
    this.patchState({ exteriorStageViewState });
  };

  public readonly updateInteriorStageViewState = (
    interiorStageViewState: StatePerScreenMode<StageViewStateValue>
  ): void => {
    this.patchState({ interiorStageViewState });
  };

  public readonly updateInteriorThreeSixtyStageViewState = (
    interiorThreeSixtyStageViewState: StatePerScreenMode<StageViewStateValue>
  ): void => {
    this.patchState({ interiorThreeSixtyStageViewState });
  };

  public readonly getViewDefinition = (stageBaseData: StageBaseData, view: StageView): StageViewDefinition =>
    stageBaseData.metaData?.views?.[view];

  public readonly getExteriorViewDefinition = (stageBaseData: StageBaseData): StageViewDefinition =>
    this.getViewDefinition(stageBaseData, StageView.Exterior360);

  public readonly getInteriorViewDefinition = (stageBaseData: StageBaseData): StageViewDefinition =>
    this.getViewDefinition(stageBaseData, StageView.Interior);

  public readonly getInteriorThreeSixtyViewDefinition = (stageBaseData: StageBaseData): StageViewDefinition =>
    this.getViewDefinition(stageBaseData, StageView.Interior360);

  public readonly initialStageViewState = (viewDefinition: StageViewDefinition): StageViewStateValue | undefined => {
    const values = Object.values(viewDefinition?.initialModeConfig?.state || []);
    if (values?.includes(StageViewStateValue.Day)) {
      return StageViewStateValue.Day;
    }
    if (values?.includes(StageViewStateValue.Night)) {
      return StageViewStateValue.Night;
    }
    return undefined; // intentionally
  };

  public readonly initialRoofState = (viewDefinition: StageViewDefinition): RoofStateValue | undefined => {
    const values = Object.values(viewDefinition?.initialModeConfig?.state || []);
    if (values?.includes(RoofStateValue.Opened)) {
      return RoofStateValue.Opened;
    }
    if (values?.includes(RoofStateValue.Closed)) {
      return RoofStateValue.Closed;
    }
    return undefined; // intentionally
  };

  public readonly determineStageViewState = (
    stageViewState: StageViewStateValue,
    stageViewDefinition: StageViewDefinition
  ): StageViewStateValue =>
    stageViewState || this.initialStageViewState(stageViewDefinition) || StageViewStateValue.Day;

  public readonly determineRoofState = (
    roofState: RoofStateValue,
    stageViewDefinition: StageViewDefinition
  ): RoofStateValue => roofState || this.initialRoofState(stageViewDefinition);

  public readonly calcExteriorThreeSixtyIndex = (perspectives: string[], perspective: string): number =>
    perspectives?.findIndex(val => val === perspective) || 0;

  public readonly calcExteriorIndex = (
    enforceReset: boolean,
    exteriorPerspectives: string[],
    exteriorThreeSixtyIndex: number,
    initialPerspective: string
  ): number =>
    enforceReset
      ? this.calcExteriorThreeSixtyIndex(exteriorPerspectives, initialPerspective)
      : (exteriorThreeSixtyIndex ?? this.calcExteriorThreeSixtyIndex(exteriorPerspectives, initialPerspective));

  public readonly calcInteriorSliderIndex = (perspectives: string[], perspective: string): number =>
    perspectives?.findIndex(val => val === perspective) || 0;

  public readonly getOptionalStageViewState = (state: StageViewStateValue[]): StageViewStateValue | undefined =>
    state?.find(item => item === StageViewStateValue.Day || item === StageViewStateValue.Night);

  public readonly getOptionalRoofState = (state: RoofStateValue[]): RoofStateValue | undefined =>
    state?.find(item => item === RoofStateValue.Opened || item === RoofStateValue.Closed);

  public isDayAndNightStageViewStateAvailable(stageStates: StageViewState[]): boolean {
    return !!stageStates?.find(
      state => state.states?.includes(StageViewStateValue.Day) && state.states?.includes(StageViewStateValue.Night)
    );
  }

  public isOpenedAndClosedRoofStateAvailable(stageStates: StageViewState[]): boolean {
    return !!stageStates?.find(
      state => state.states?.includes(RoofStateValue.Opened) && state.states?.includes(RoofStateValue.Closed)
    );
  }

  public readonly updateStageRatio = (stageRatio: StageRatio): void => {
    this.patchState({ stageRatio });
  };

  public readonly updateMediaQuery = (mediaQuery: Mq): void => {
    this.patchState({ mediaQuery });
  };

  public readonly updateButtonTooltipTexts = (buttonTooltipTexts: ButtonTooltipTexts): void => {
    this.patchState({ buttonTooltipTexts });
  };

  public readonly updateExteriorRoofState = (exteriorRoofState: StatePerScreenMode<RoofStateValue>): void => {
    this.patchState({ exteriorRoofState });
  };

  public readonly updateInteriorRoofState = (interiorRoofState: StatePerScreenMode<RoofStateValue>): void => {
    this.patchState({ interiorRoofState });
  };

  public readonly updateInteriorThreeSixtyRoofState = (
    interiorThreeSixtyRoofState: StatePerScreenMode<RoofStateValue>
  ): void => {
    this.patchState({ interiorThreeSixtyRoofState });
  };

  public readonly updateExteriorThreeSixtyStageLoaded = (isExteriorThreeSixtyStageLoaded: boolean): void => {
    this.patchState({ isExteriorThreeSixtyStageLoaded });
  };

  public readonly updateExteriorThreeSixtyIndex = (exteriorThreeSixtyIndex: number): void => {
    this.patchState({ exteriorThreeSixtyIndex });
  };

  public readonly updateInteriorSliderIndex = (interiorSliderIndex: number): void => {
    this.patchState({ interiorSliderIndex });
  };

  public readonly updateIsInteriorThreeSixtyLoading = (isInteriorThreeSixtyLoading: boolean): void => {
    this.patchState({ isInteriorThreeSixtyLoading });
  };

  public readonly updateIsInteriorThreeSixtyError = (isInteriorThreeSixtyError: boolean): void => {
    this.patchState({ isInteriorThreeSixtyError });
  };

  public readonly switchToInteriorView = (): void => {
    this.patchState({ isInThreeSixtyInteriorMode: false, currentView: StageView.Interior });
  };

  public readonly updatePannellumViewer = (pannellumViewer: PannellumViewer): void => {
    // Fuck Pannellum!
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.patchState({ pannellumViewer });
  };

  public createImgSrc(
    cosyId: string,
    hasImageError: boolean,
    baseInfo: StageV2BaseInformation,
    imgType: string,
    perspective: string,
    stageViewState: StageViewStateValue,
    roofState: RoofStateValue,
    additionalParameters: StageParameter[],
    imageWidth: number
  ): string {
    const builder = hasImageError
      ? new BbdUrlBuilder(baseInfo.fallbackImageUrl)
      : this.createImgUrlBuilder(
          baseInfo?.baseUrl,
          cosyId,
          imgType,
          perspective,
          stageViewState,
          roofState,
          additionalParameters
        );
    builder.appendImgWidth(imageWidth.toString());
    return builder.toString();
  }

  public createImgUrlBuilder(
    baseUrl: string,
    cosyId: string,
    imgType: string,
    perspective: string,
    viewState: StageViewStateValue,
    roofState: RoofStateValue,
    additionalParameters: StageParameter[]
  ): BbdUrlBuilder {
    return new BbdUrlBuilder(baseUrl)
      .appendCosyId(cosyId)
      .appendImgType(imgType)
      .appendPov(createCacheOptimizedPovParam(perspective, viewState, roofState))
      .appendGeneric(stageParamsToQueryParams(additionalParameters));
  }

  /**
   * @returns the best matching image type value (e.g. 'W27' for webp). webp is preferred over png (if webp available)
   */
  public getImageType(imageTypes: StageImageType[]): string | undefined {
    const find = (type: string): StageImageType | undefined =>
      imageTypes?.find(imageType => imageType.mimeType === type);
    const format = find(MimeType.WebP) || find(MimeType.Png) || find(MimeType.Jpeg);
    return format?.value || imageTypes?.[0]?.value || undefined; // fallback might not be ideal, to be checked.
  }

  public buildImageParameters(
    viewDefinition: StageViewDefinition,
    stageBaseData: StageBaseData,
    baseInfo: StageV2BaseInformation,
    perspectives: string[],
    stageViewState: StageViewStateValue,
    roofState: RoofStateValue,
    isInFullscreenMode: boolean,
    imageWidth: number
  ): ImageParameters {
    const imgType = this.getImageType(viewDefinition?.imageTypes);
    return {
      cosyId: stageBaseData?.cosyId,
      hasImageError: stageBaseData?.hasImageError,
      baseInfo,
      imgType,
      isInFullscreenMode,
      imageWidth,
      perspectives,
      stageViewState,
      roofState,
      additionalParameters:
        isInFullscreenMode && viewDefinition?.fullScreenDefinition
          ? viewDefinition?.fullScreenDefinition.additionalParameters
          : viewDefinition?.additionalParameters
    };
  }

  private getElementHeight(element: HTMLElement): number {
    return (element?.getBoundingClientRect().toJSON() as DOMRectReadOnly)?.height || 0;
  }

  private cancelFullscreen(): void {
    const cancellFullscreen =
      (this.document.exitFullscreen && 'exitFullscreen') ||
      (this.document.mozCancelFullScreen && 'mozCancelFullScreen') ||
      (this.document.webkitExitFullscreen && 'webkitExitFullscreen') ||
      (this.document.msExitFullscreen && 'msExitFullscreen');
    ((this.document[cancellFullscreen] as () => Promise<void>).call(this.document) as Promise<void>).catch(noop);
  }

  private requestFullscreen(element: HTMLElement): void {
    const requestFullscreen =
      (element.requestFullscreen && 'requestFullscreen') ||
      (element.webkitRequestFullScreen && 'webkitRequestFullScreen') ||
      (element.mozRequestFullScreen && 'mozRequestFullScreen') ||
      (element.msRequestFullScreen && 'msRequestFullScreen');
    ((element[requestFullscreen] as () => Promise<void>).call(element) as Promise<void>).catch(noop);
  }
}
